import api from "./api";

export function passkeysSupported() {
  return typeof window !== "undefined" && window.isSecureContext &&
    Boolean(window.PublicKeyCredential && navigator.credentials?.create && navigator.credentials?.get);
}

export function decodeBase64url(value) {
  const binary = atob(value.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - value.length % 4) % 4));
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

export function encodeBase64url(value) {
  if (value == null) return null;
  const bytes = new Uint8Array(value);
  return btoa(Array.from(bytes, (b) => String.fromCharCode(b)).join("")).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function publicKeyOptions(options) {
  const result = { ...options, challenge: decodeBase64url(options.challenge) };
  if (options.user) result.user = { ...options.user, id: decodeBase64url(options.user.id) };
  for (const key of ["excludeCredentials", "allowCredentials"]) {
    if (options[key]) result[key] = options[key].map((c) => ({ ...c, id: decodeBase64url(c.id) }));
  }
  return result;
}

export function serializeCredential(credential) {
  const response = {};
  for (const field of ["clientDataJSON", "attestationObject", "authenticatorData", "signature", "userHandle"]) {
    if (credential.response[field] != null) response[field] = encodeBase64url(credential.response[field]);
  }
  if (credential.response.getTransports) response.transports = credential.response.getTransports();
  return { id: credential.id, rawId: encodeBase64url(credential.rawId), type: credential.type, response,
    clientExtensionResults: credential.getClientExtensionResults?.() || {}, authenticatorAttachment: credential.authenticatorAttachment || undefined };
}

export function passkeyError(error) {
  if (error?.name === "NotAllowedError") return "Passkey sign-in was cancelled or timed out. Try again, or use your email and password.";
  if (error?.name === "InvalidStateError") return "A passkey for this account already exists on this device.";
  if (error?.name === "SecurityError") return "Open sophiasweeps.com directly in Chrome or Safari to use a passkey.";
  return null;
}

export async function signInWithPasskey() {
  if (!passkeysSupported()) throw new Error("Open Sophia in Chrome or Safari to use a passkey.");
  const { data } = await api.post("/auth/passkeys/login/options");
  const credential = await navigator.credentials.get({ publicKey: publicKeyOptions(data.options) });
  if (!credential) throw new Error("No passkey was selected.");
  return (await api.post("/auth/passkeys/login/verify", { ceremony_id: data.ceremony_id, credential: serializeCredential(credential) })).data;
}

export async function createPasskey(name, password) {
  if (!passkeysSupported()) throw new Error("Open Sophia in Chrome or Safari to create a passkey.");
  const { data } = await api.post("/auth/passkeys/register/options", { password: password || undefined });
  const credential = await navigator.credentials.create({ publicKey: publicKeyOptions(data.options) });
  if (!credential) throw new Error("Passkey creation was cancelled.");
  return (await api.post("/auth/passkeys/register/verify", { ceremony_id: data.ceremony_id, name, credential: serializeCredential(credential) })).data;
}
